import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { COMPANY, SUPPORT_EMAIL } from '../data/launch';

/**
 * INTERNAL NOTE — not rendered.
 * Contact matching, receipt OCR, receipt visibility, bank-detail visibility,
 * device location, currency behaviour, security controls and account-deletion
 * behaviour are all now VERIFIED against the production app and backend, and
 * the sections below describe the verified behaviour.
 * Still outstanding for legal sign-off, tracked in
 * docs/internal-pre-launch-checklist.md: exact retention periods, named
 * processors, minimum age, and international transfer wording. Do not surface
 * those uncertainties to visitors.
 */
export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-611" data-mp="1wzjkqjyzzm9g">
      <SEO title="Privacy Policy | KittyPot" description="How KittyPot Ltd collects, uses and protects your personal data in the KittyPot app and on kittypot.com, written in plain UK English." ogTitle="KittyPot Privacy Policy" ogDescription="How KittyPot handles your personal data." path="/privacy-policy" data-id="element-612" data-mp="50hldjhyz3l2" />
      <Navbar data-id="element-613" data-mp="8hndwlgmkr4" />
      <main className="pt-32 pb-24" data-id="element-614" data-mp="7y9cqyxkgid">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-615" data-mp="3ifny39p2ayhd">
          <h1 className="text-4xl font-black text-kitty-dark mb-4" data-id="element-616" data-mp="3nf0kxufxo5jy">
            Privacy Policy
          </h1>
          <p className="text-gray-500 font-medium mb-10" data-id="element-617" data-mp="38hzqvka9ryib">
            Last updated: 26 August 2026
          </p>

          <div className="prose prose-gray max-w-none text-gray-600 space-y-6" data-id="element-618" data-mp="3dgb1vycawf24">
            <p data-id="element-619" data-mp="16qd6i1kvv1ut">
              This policy explains what personal data {COMPANY.legalName}{' '}
              collects when you use the KittyPot app or our website, why we
              collect it, and what rights you have. It is written in plain UK
              English.
            </p>

            <p data-id="element-620" data-mp="1a5y1ota956pn">
              KittyPot helps groups record shared expenses, split costs and keep
              track of who owes what. KittyPot does not hold or move your money.
              Group members settle up directly with each other.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-621" data-mp="w9ugtql9qapu">
              1. Who we are
            </h2>
            <p data-id="element-622" data-mp="1t0whfwah9hpx">
              {COMPANY.legalName} (company number {COMPANY.registrationNumber})
              is the data controller for the personal data described in this
              policy. You can reach us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-623" data-mp="1f7dksfsvd35e">
                {SUPPORT_EMAIL}
              </a>{' '}
              or by post:
            </p>
            <address className="not-italic" data-id="element-624" data-mp="26wqffk9pqmo9">
              {COMPANY.addressLines.map(line => <React.Fragment key={line} data-id="element-625" data-mp="3pnnxdviqqy38">
                  {line}
                  <br data-id="element-626" data-mp="3ukxrvpkzlzxx" />
                </React.Fragment>)}
            </address>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-627" data-mp="2f236usm0lwp">
              2. Account information
            </h2>
            <p data-id="element-628" data-mp="7dbz57m5rqjd">
              When you create a KittyPot account we process the information
              needed to identify you and keep your account secure. Depending on
              how you sign up, this may include:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-629" data-mp="m4cgy38sows6">
              <li data-id="element-630" data-mp="m6p733opidgb">your name or display name</li>
              <li data-id="element-631" data-mp="q6fvj0qrghho">your email address</li>
              <li data-id="element-632" data-mp="q5em10pe8322">a phone number, where you choose to provide one</li>
              <li data-id="element-633" data-mp="v6c319yo2r1z">a profile picture, where you choose to add one</li>
              <li data-id="element-634" data-mp="104400ie653d1">
                authentication and account identifiers, including identifiers
                supplied by Google, Apple or Facebook if you sign in with one of
                those services
              </li>
            </ul>
            <p data-id="element-635" data-mp="hi0rkjv4wuri">
              If you sign in with Google, Apple or Facebook, that provider tells
              us enough to identify your account. We do not receive your
              password, and we do not post anything to those accounts.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-636" data-mp="3i96jz67ae40">
              3. Kitty and expense information
            </h2>
            <p data-id="element-637" data-mp="3uqn1wrjebu5v">
              To provide the core service we process the content you and other
              members add to a kitty:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-638" data-mp="8i5cw52dehmq">
              <li data-id="element-639" data-mp="8gativ6qyxkr">kitty names and the members of each kitty</li>
              <li data-id="element-640" data-mp="1ovc9yq5do1x4">expenses, including description, amount and date</li>
              <li data-id="element-641" data-mp="1jx10whmwpvfy">who paid, and who an expense is shared with</li>
              <li data-id="element-642" data-mp="1jwraisxy9m9q">split information, whether equal or custom amounts</li>
              <li data-id="element-643" data-mp="1rficqui99mc3">balances between members</li>
              <li data-id="element-644" data-mp="1riwga62no1l4">
                settlement records, including amounts marked as paid and
                confirmations that a payment was received
              </li>
            </ul>
            <p data-id="element-645" data-mp="1ihyc9ns7vu2j">
              This information is shared with the other members of that kitty,
              because a shared expense record only works if the group can see
              it. It is not made public.
            </p>
            <p data-id="element-646" data-mp="2f5uwd2mv57ii">
              Some of your account information is also visible to the other
              active members of a kitty you are in, so the group can tell who is
              who. This includes your display name and profile picture, and can
              include the email address on your account as part of the member
              information for that kitty. People who have been invited but have
              not yet joined do not receive this.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-647" data-mp="2xygnj9mts9wr">
              4. Contact matching
            </h2>
            <p data-id="element-648" data-mp="33rahi2c1mumz">
              Giving the app permission to access your contacts is optional, and
              the access is read only. Where you use it, the app reads the
              names, phone numbers and email addresses it needs for inviting
              people.
            </p>
            <p data-id="element-649" data-mp="2otqev22mdbjz">
              To check whether people you already know are on KittyPot, only
              phone numbers and email addresses are sent to us, over an
              encrypted connection. Contact names, photos, addresses and notes
              are not sent as part of that check. We use those identifiers only
              to find existing KittyPot accounts that match information already
              in your contacts, and we return matches to your device.
            </p>
            <p data-id="element-650" data-mp="2653a6kocuo6x">
              We do not store that list of phone numbers and email addresses. It
              exists on our systems only for as long as the check takes, and is
              not kept afterwards. We do not build an uploaded copy of your
              address book, and we do not use your contacts for advertising or
              scoring. We also do not build a public directory or let anyone
              browse a list of all KittyPot users.
            </p>
            <p data-id="element-651" data-mp="25wq7ds79mq4u">
              You can use KittyPot without granting contacts permission, and you
              can withdraw the permission at any time in your device settings.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-652" data-mp="1d9catv7hbwl9">
              5. Receipts
            </h2>
            <p data-id="element-653" data-mp="1i5ua4fk1j2q7">
              You can optionally attach a receipt image to an expense. If you
              scan a receipt, the text is read on your own device, using text
              recognition built into the app. The image is not sent to us for
              that step, and the details it suggests are only a starting point
              for you to check.
            </p>
            <p data-id="element-654" data-mp="28pb6x5cz63i6">
              When you save the expense, the receipt image is uploaded to
              KittyPot so it stays attached to that shared expense and the group
              can refer back to it. Details you confirm from the receipt, such
              as line items, the receipt date and any tax or service amount, may
              be stored as part of the expense.
            </p>
            <p data-id="element-655" data-mp="2rm41r61sjcem">
              Receipts are not public. Only active members of that kitty can
              view them, through time-limited links that are generated when
              needed. Deleting an expense also deletes the receipt attached to
              it. If you delete your account, a receipt attached to a shared
              expense may remain as part of that shared record, as described in
              section 16.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-656" data-mp="36ctg2hajt7mp">
              6. Bank details
            </h2>
            <p data-id="element-657" data-mp="3py3dud0hruew">
              You may choose to save bank details, such as an account name, sort
              code and account number, so they are easy to share when your group
              settles up. This is entirely optional, and you control whether
              they are shared.
            </p>
            <p data-id="element-658" data-mp="3kyh104oksg6m">
              Where you turn sharing on, those details may be shown to the other
              active members of a shared kitty, so they can pay you back. People
              who have been invited but have not yet joined do not receive them.
              They are never displayed publicly, and we do not sell them. They
              are sent over encrypted connections.
            </p>
            <p data-id="element-659" data-mp="329tz1v1p46x9">
              KittyPot does not use these details to make or start a payment.
              Any payment between group members happens directly between you,
              outside KittyPot.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-660" data-mp="2sa345s983pwn">
              7. Currency preferences
            </h2>
            <p data-id="element-661" data-mp="3a230uovba7ug">
              Each kitty runs in its own currency, and every expense in it is
              recorded in that currency. Separately, you can set a display
              currency for your own account, which is used to show estimated
              totals in My Balance. Changing it does not change the currency a
              kitty runs in, and it does not change any recorded amount.
            </p>
            <p data-id="element-662" data-mp="1jnf4h1kasyc">
              We process that preference and the conversion figures available to
              us, including any you supply, to produce those estimates.
              Converted amounts are estimates for presentation only and may not
              match a bank or provider rate at the time you settle.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-663" data-mp="fin2lldc91sx">
              8. Device information and notifications
            </h2>
            <p data-id="element-664" data-mp="kc97grd9g1e6">
              To deliver and support the app we may process technical
              information such as your device type and operating system, the app
              version you are running, and a push notification token if you turn
              notifications on. We also process basic diagnostic information so
              we can find and fix problems.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-665" data-mp="29tmuw26wvtms">
              9. Reporting an issue
            </h2>
            <p data-id="element-666" data-mp="1h34i29gdydw6">
              If you report a problem, we process what you send us: your
              description of the issue, any screenshot you choose to attach, and
              diagnostic details such as your internal user identifier, device
              type, app version and error information. We use this only to
              investigate and resolve the issue.
            </p>
            <p data-id="element-667" data-mp="32g7mn3rau7qv">
              The same applies to anything else you send us. If you email
              support, we keep that correspondence so we can help you and refer
              back to it if you contact us again. Please do not send us
              passwords or full bank details, and do not include them in a
              screenshot.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-668" data-mp="293zhy17xm3w2">
              10. How you found us, and location
            </h2>
            <p data-id="element-669" data-mp="1gesld7s1fodg">
              We may record privacy-friendly information about how an account
              was created, such as an invite or campaign code, the registration
              source, and the platform or store you installed from. This helps
              us understand which routes into KittyPot work well. None of this
              involves tracking where you are.
            </p>
            <p data-id="element-670" data-mp="xoatt6ixyyto">
              KittyPot does not collect your device location. The app does not
              ask for location permission on either Android or iPhone, and it
              does not collect a precise or an approximate position from your
              device. Our website runs no location-tracking or analytics
              scripts.
            </p>
            <p data-id="element-671" data-mp="p7to3iepu9kg">
              Like any internet service, our systems receive your IP address
              when your device connects to us. It is used to deliver the service
              and to keep it secure. An IP address can indicate an approximate
              region, such as a country or city, and where it is used in that
              way it is only ever at that coarse level, never a precise
              position.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-672" data-mp="1hpp5och2xzs">
              11. Analytics
            </h2>
            <p data-id="element-673" data-mp="3tmbdfnaiyrr7">
              Where we use analytics, it is to understand how KittyPot is used
              in aggregate, such as which features are used and where people
              encounter problems, so we can improve the product. Our website
              currently runs no analytics or advertising scripts at all; the
              technologies it does use are set out in our Cookie Policy.
            </p>
            {/* INTERNAL: name the analytics providers actually in use once
                confirmed. The website itself currently loads none. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-674" data-mp="1dsk13venz5da">
              12. Why we process your data, and our lawful bases
            </h2>
            <ul className="list-disc pl-5 space-y-2" data-id="element-675" data-mp="14sx0hbtre3la">
              <li data-id="element-676" data-mp="18vbffkub4j7a">
                <strong data-id="element-677" data-mp="18vjwloxgqaoa">To provide the service</strong>, creating your account,
                running your kitties, recording expenses, balances and
                settlements. Lawful basis: performance of a contract with you.
              </li>
              <li data-id="element-678" data-mp="zuveoj4y7q64">
                <strong data-id="element-679" data-mp="zwga0az37d51">To keep the service secure and working</strong>,
                diagnostics, abuse prevention and fixing faults. Lawful basis:
                our legitimate interests in running a secure, reliable service.
              </li>
              <li data-id="element-680" data-mp="imccf7try4rw">
                <strong data-id="element-681" data-mp="ilkj63nngj4s">To improve KittyPot</strong>, aggregate analytics and
                understanding how features are used. Lawful basis: our
                legitimate interests, or your consent where required for the
                technology used.
              </li>
              <li data-id="element-682" data-mp="njckfoa6phln">
                <strong data-id="element-683" data-mp="nit060x2ah4g">Contact matching, notifications and receipts</strong>,
                optional features you switch on. Lawful basis: your consent,
                given through the permission or setting.
              </li>
              <li data-id="element-684" data-mp="wf25fw7q09yi">
                <strong data-id="element-685" data-mp="1bai1g6rv2fxj">Product and launch emails you asked for</strong>,
                waitlist and product updates. Lawful basis: your consent.
              </li>
              <li data-id="element-686" data-mp="168slqdtp1e34">
                <strong data-id="element-687" data-mp="3d1bnncwctvei">Meeting our legal obligations</strong>, for example
                responding to a lawful request. Lawful basis: legal obligation.
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-688" data-mp="384l3oehfifnt">
              13. Service providers
            </h2>
            <p data-id="element-689" data-mp="5m1vk2eh2wss">
              We use trusted service providers to operate KittyPot, for example
              to host our systems, deliver notifications and provide analytics.
              They process personal data on our instructions and under
              appropriate contractual terms, and they may not use it for their
              own purposes.
            </p>
            <p data-id="element-690" data-mp="ki19vus6dt6d">
              One provider is worth naming specifically. If you sign up to our
              waitlist on this website, your name, email address and marketing
              choice are submitted through Google infrastructure, which we use
              to collect and store waitlist entries. Google processes that
              submission on our behalf so we can contact you about the launch.
            </p>
            {/* INTERNAL: list the actual processors and their roles once
                confirmed. No providers are named that we cannot verify. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-691" data-mp="2sssfea4fxxd6">
              14. International transfers
            </h2>
            <p data-id="element-692" data-mp="2itb67ipzd1cg">
              Some of our service providers may process data outside the United
              Kingdom. Where that happens, we will put in place the safeguards
              required by UK data protection law so that your data continues to
              receive an appropriate level of protection.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-693" data-mp="36jx5ollx5dau">
              15. How long we keep data
            </h2>
            <p data-id="element-694" data-mp="31qchtme1hzpd">
              We keep personal data only for as long as we need it for the
              purpose it was collected for, or for as long as we are required to
              keep it by law. Kitty and expense records are kept while the kitty
              is in use and afterwards so that members can refer back to a
              shared history. When data is no longer needed, we delete or
              anonymise it.
            </p>
            {/* INTERNAL: set and state specific retention periods for
                accounts, kitty history, receipts and diagnostics. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-695" data-mp="2ov1sv7u2gb23">
              16. Deleting your account
            </h2>
            <p data-id="element-696" data-mp="2tq7avvkcl5kw">
              You can delete your account from within the app, or by emailing us
              from the address on your account. Deleting your account removes or
              anonymises your account information and prevents the account from
              being used again. Some information that forms part of a shared
              kitty's expense or settlement history may remain, so the records
              of other group members continue to work correctly.
            </p>
            <p data-id="element-697" data-mp="1w7602e7mb3al">In practice that means:</p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-698" data-mp="21464kkotjxzl">
              <li data-id="element-699" data-mp="211jmvs87s23n">the account can no longer be used to sign in</li>
              <li data-id="element-700" data-mp="1tj2czkwfela5">
                your core personal account information is removed or anonymised,
                including your phone number, date of birth, profile picture,
                authentication details and any saved bank or payment details
              </li>
              <li data-id="element-701" data-mp="1xlqipjpufc2w">
                shared expense, settlement and membership records may remain in
                the kitties you took part in, so other members' shared financial
                history still makes sense
              </li>
              <li data-id="element-702" data-mp="15wnda0m7zvoo">
                a historical display name may remain against those shared
                records, so the group can still tell which entries were yours
              </li>
              <li data-id="element-703" data-mp="1etxsty62m6ze">
                a receipt image attached to a shared expense may remain as part
                of that expense
              </li>
              <li data-id="element-704" data-mp="m735mthvcti8">
                we may keep a limited technical identifier, in a form that is
                not readable as your details, so that a previously deleted
                account can be recognised if the same details are used again
              </li>
              <li data-id="element-705" data-mp="39qvhfwf4ybuq">
                we may retain limited information where we are required to by
                law
              </li>
            </ul>
            <p data-id="element-706" data-mp="3jqur5wnak896">
              Anything no longer needed for those purposes is deleted or
              anonymised. We cannot remove an expense from someone else's
              records where doing so would make their shared history wrong. If
              you would like to know what remains after deletion, email us and
              we will explain.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-707" data-mp="6c8283sj5vyt">
              17. Your rights
            </h2>
            <p data-id="element-708" data-mp="2d1w6g9n62vyu">Under UK data protection law you have the right to:</p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-709" data-mp="283kx000sanh5">
              <li data-id="element-710" data-mp="284w86kwk6xia">ask for a copy of the personal data we hold about you</li>
              <li data-id="element-711" data-mp="nlae88j7azfz">ask us to correct information that is wrong</li>
              <li data-id="element-712" data-mp="sknafjvypiv8">ask us to delete your data, subject to section 16</li>
              <li data-id="element-713" data-mp="sjvci2ew791s">ask us to restrict how we use your data</li>
              <li data-id="element-714" data-mp="dwin22lysj64">
                object to processing we carry out on the basis of legitimate
                interests
              </li>
              <li data-id="element-715" data-mp="isqzv9cow0a4">
                ask for your data in a portable format, where that right applies
              </li>
              <li data-id="element-716" data-mp="9u5l7pac95em">
                withdraw your consent at any time where we rely on consent
              </li>
            </ul>
            <p data-id="element-717" data-mp="3r32bo2g3wblk">
              To exercise any of these, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-718" data-mp="3w1dlweabco4b">
                {SUPPORT_EMAIL}
              </a>
              . If you are unhappy with how we have handled your data you can
              complain to the Information Commissioner's Office at ico.org.uk.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-719" data-mp="1nt7eccv86fpz">
              18. Children
            </h2>
            <p data-id="element-720" data-mp="1ivp1wci01id8">
              You must be at least 13 years old to create or use a KittyPot
              account. If you are under 18, you should only use KittyPot where
              you have the permission of a parent or guardian where required by
              applicable law.
            </p>
            <p data-id="element-721" data-mp="26jylr8vnjw1q">
              KittyPot is not designed for younger children, and we do not
              knowingly collect personal data from anyone under 13. If you
              believe a child under that age has created an account, contact us
              and we will look into it.
            </p>
            {/* INTERNAL: 13+ is confirmed. Make sure it stays consistent with
                the age rating declared on Google Play and the App Store. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-722" data-mp="c0g5kkdoc774">
              19. Security
            </h2>
            <p data-id="element-723" data-mp="gz16yn4nwwx5">
              We take reasonable technical and organisational measures to
              protect your data. Traffic between the app and our systems uses
              encrypted connections, access requires you to be signed in, and
              what you can see is controlled by which kitties you are an active
              member of and what role you have in them. Receipt images are
              reached through time-limited links rather than being openly
              accessible.
            </p>
            <p data-id="element-724" data-mp="14ovlra8jkvk1">
              No online service can promise perfect security, so we do not claim
              to be completely secure, but we work to keep your data safe and to
              respond quickly if something goes wrong.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-725" data-mp="1dnpegpir51sb">
              20. Changes to this policy
            </h2>
            <p data-id="element-726" data-mp="151o1a5arw53w">
              We may update this policy as KittyPot develops. When we make a
              material change we will update the date at the top of this page
              and, where appropriate, let you know in the app.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-727" data-mp="1er7kxd6b82zt">
              21. Contact us
            </h2>
            <p data-id="element-728" data-mp="19v8tfgpoq4v6">
              For any privacy question, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-729" data-mp="m3tuzz1147p7">
                {SUPPORT_EMAIL}
              </a>{' '}
              or write to us at the address in section 1.
            </p>
          </div>
        </div>
      </main>
      <Footer data-id="element-730" data-mp="27fvdltte1une" />
    </div>;
}